import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PromoModal } from "./PromoModal.js";

const SEEN_STORAGE_KEY = "tgpe:promo_button_seen";

/**
 * Tiny header button: a gold rhombus that subtly pulses until the user
 * has clicked it at least once on this device. Opens the cross-promo
 * modal so the user can discover the rest of the giglabo product line
 * without leaving the editor.
 *
 * Pulse state persists in localStorage — once the user has acknowledged
 * the button, it stays calm forever (per device).
 */
export function PromoButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState<boolean>(() => readSeen());

  const markSeen = useCallback(() => {
    if (seen) return;
    try {
      localStorage.setItem(SEEN_STORAGE_KEY, "1");
    } catch {
      // Private mode / quota exhausted — fall through; the in-memory
      // state below still suppresses the animation for this session.
    }
    setSeen(true);
  }, [seen]);

  // Cross-tab consistency: if the user opened the modal in another tab,
  // calm the icon here too.
  useEffect(() => {
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === SEEN_STORAGE_KEY && ev.newValue) setSeen(true);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return (
    <>
      <button
        type="button"
        aria-label={t("promo.open")}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--tg-secondary-bg)]"
        onClick={() => {
          markSeen();
          setOpen(true);
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          aria-hidden
          className={seen ? undefined : "promo-romb-pulse"}
        >
          {/*
            Romb (rhombus) — a 12-unit square rotated 45° around (8,8).
            Two-tone: bright gold core, slightly darker outline so it stays
            legible against both the light and dark Telegram themes.
          */}
          <g transform="rotate(45 8 8)">
            <rect
              x="2"
              y="2"
              width="12"
              height="12"
              rx="1.5"
              fill="#facc15"
              stroke="#a16207"
              strokeWidth="1"
            />
          </g>
        </svg>
      </button>
      <PromoModal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </>
  );
}

function readSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}
